import { CORPUS_TEXT_LIMIT } from "@/app/_lib/utils/corpus-validation.utils";

// Mirrors the sampleDocuments zod bounds — seeds flow into saved samples.
const MAX_FILE_NAME_LENGTH = 200;
const MAX_FORMAT_LENGTH = 20;

export const MAX_SEED_DOCS = 50;

export type TrainingSeedRow = {
  id: string;
  publicId: string;
  secureUrl: string;
  documentText: string | null;
};

export type TrainingSeedDoc = {
  fileName: string;
  publicId: string;
  secureUrl: string;
  format?: string;
  text?: string;
};

/**
 * Maps a saved Document row to the corpus-seed shape. Document rows have no
 * fileName or format columns: the name falls back to the last segment of the
 * Cloudinary publicId (how the documents UI labels them), and the format is
 * read off the secureUrl extension when one is present. Text is omitted when
 * the row never extracted any, so the corpus session re-extracts via the
 * secureUrl instead of treating the document as read.
 */
export const documentToSeedDoc = (row: TrainingSeedRow): TrainingSeedDoc => {
  const segments = row.publicId.split("/");
  const fileName = (segments[segments.length - 1] || row.publicId).slice(
    0,
    MAX_FILE_NAME_LENGTH
  );

  let format: string | undefined;
  const urlPath = row.secureUrl.split("?")[0];
  const lastDot = urlPath.lastIndexOf(".");
  const lastSlash = urlPath.lastIndexOf("/");
  if (lastDot > lastSlash && lastDot < urlPath.length - 1) {
    const extension = urlPath.slice(lastDot + 1).toLowerCase();
    if (extension.length <= MAX_FORMAT_LENGTH) format = extension;
  }

  const text = (row.documentText ?? "").slice(0, CORPUS_TEXT_LIMIT);

  return {
    fileName,
    publicId: row.publicId,
    secureUrl: row.secureUrl,
    ...(format && { format }),
    ...(text && { text }),
  };
};

const OBJECT_ID_REGEX = /^[0-9a-f]{24}$/;

/**
 * Parses the `?docs=` query param (comma-separated Mongo ObjectIds) into a
 * deduped, validated id list capped at MAX_SEED_DOCS. Garbage segments are
 * dropped rather than failing the page.
 */
export const parseDocsParam = (raw: string | undefined | null): string[] => {
  if (!raw) return [];
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const segment of raw.split(",")) {
    const id = segment.trim();
    if (!OBJECT_ID_REGEX.test(id) || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
    if (ids.length >= MAX_SEED_DOCS) break;
  }
  return ids;
};
